import { FieldMask } from '@weave/schema-gen-ts/dist/google/protobuf/field_mask.pb';
import { PositionAnnouncementOptions } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config.pb';
import { UpdateV2Request } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config_service.pb';
import { i18next } from '@frontend/i18n';
import { FormValues, UseFormReturnType } from '@frontend/design-system';
import { DEFAULT_STANDARD_MEDIA } from '../media-picker';
import { CallQueueCreatePayload, CallQueueDevice, CallQueueRingType, CallQueueState } from './types';

export const schema = {
  // id: {
  //   type: 'text',
  //   required: false,
  // },
  name: {
    type: 'text',
    required: true,
    value: '' as string,
  },
  deviceRingType: {
    type: 'radio',
    required: true,
    value: CallQueueRingType.RING_TYPE_RING_ALL as string,
  },
  greetingEnabled: {
    type: 'checkbox',
    required: false,
    value: false as boolean,
  },
  greetingMediaId: {
    type: 'dropdown',
    required: false,
    value: '' as string,
  },
  allowExit: {
    type: 'checkbox',
    required: false,
    value: false as boolean,
  },
  holdMusicId: {
    type: 'dropdown',
    required: false,
    value: '' as string,
  },
  // This field is hidden from users
  holdMusicSystemMedia: {
    type: 'switch',
    required: false,
    value: false as boolean,
  },
  // assignedDevices: {
  //   type: 'multiselect',
  //   required: false,
  //   value: [] as string[],
  // },
  positionAnnouncementEnabled: {
    type: 'checkbox',
    required: false,
    value: false as boolean,
  },
  firstAnnouncementDelayMs: {
    type: 'dropdown',
    required: false,
    value: '0' as string,
  },
  repeatIntervalMs: {
    type: 'dropdown',
    required: false,
    value: '0' as string,
  },
  maxWaitTimeMs: {
    type: 'text',
    required: false,
    value: '0' as string,
  },
  locationIds: {
    type: 'multiselect',
    required: false,
    value: [] as string[],
  },
} as const;

export const populateSchema = (state: CallQueueCreatePayload) => {
  const res = { ...schema };

  let key: keyof typeof schema;
  for (key in schema) {
    if (key === 'maxWaitTimeMs') {
      res[key] = { ...res[key], value: (state[key] / 1000 / 60).toString() || '0' };
    } else if (key === 'repeatIntervalMs' || key == 'firstAnnouncementDelayMs') {
      res[key] = { ...res[key], value: ((state.positionAnnouncement?.[key] ?? 0) / 1000)?.toString() || '0' };
    } else if (key === 'holdMusicId') {
      res[key] = { ...res[key], value: state[key] || DEFAULT_STANDARD_MEDIA };
    } else if (key === 'holdMusicSystemMedia') {
      // Default to true since the default media selection is a system media
      res[key] = { ...res[key], value: state[key] ?? true };
    } else {
      // @ts-ignore - this is fine!
      res[key] = { ...res[key], value: state[key] };
    }
  }

  return res;
};

export const createSubmitPayload = ({
  formValues,
  state,
}: {
  formValues: FormValues<typeof schema>;
  state: CallQueueState;
}) => {
  const {
    firstAnnouncementDelayMs,
    repeatIntervalMs,
    maxWaitTimeMs,
    greetingMediaId,
    holdMusicId,
    deviceRingType,
    ...rest
  } = formValues;
  return {
    ...state,
    ...rest,
    // Convert `default` to empty string - `default` is for the media picker, but the API expects an empty string
    greetingMediaId: greetingMediaId === 'default' ? '' : greetingMediaId ?? '',
    holdMusicId: holdMusicId === 'default' ? '' : holdMusicId ?? '',
    positionAnnouncement: {
      firstAnnouncementDelayMs: parseInt(firstAnnouncementDelayMs || '0') * 1000,
      repeatIntervalMs: parseInt(repeatIntervalMs || '0') * 1000,
    },
    maxWaitTimeMs: parseInt(maxWaitTimeMs || '0') * 1000 * 60,
    deviceRingType: deviceRingType as CallQueueRingType,
  };
};

export type SchemaForm = UseFormReturnType<typeof schema, keyof typeof schema>;
export type SchemaFormValues = FormValues<typeof schema>;

// Maps form fields to the UpdateV2 FieldMask path(s) that must be included in the update
// when that field changes. Fields that only control UI visibility for another field
// (e.g. `greetingEnabled`, `positionAnnouncementEnabled`, `holdMusicSystemMedia`) share the
// same mask path as the field they control, since the API has no dedicated path for them.
const FORM_FIELD_TO_MASK_PATH: Partial<Record<keyof typeof schema, string>> = {
  name: 'name',
  deviceRingType: 'ringType',
  greetingEnabled: 'greetingMediaId',
  greetingMediaId: 'greetingMediaId',
  allowExit: 'allowExit',
  holdMusicId: 'holdMusicId',
  holdMusicSystemMedia: 'holdMusicId',
  positionAnnouncementEnabled: 'positionAnnouncement',
  firstAnnouncementDelayMs: 'positionAnnouncement',
  repeatIntervalMs: 'positionAnnouncement',
  maxWaitTimeMs: 'maxWaitTimeMs',
  locationIds: 'locationIds',
};

export const buildCallQueueUpdateMaskPaths = (
  changedValues: Partial<Record<keyof typeof schema, unknown>> | null | undefined,
  hasDeviceChanges: boolean
): string[] => {
  const paths = new Set<string>();

  (Object.keys(changedValues ?? {}) as (keyof typeof schema)[]).forEach((key) => {
    const path = FORM_FIELD_TO_MASK_PATH[key];
    if (path) {
      paths.add(path);
    }
  });

  if (hasDeviceChanges) {
    paths.add('devices');
  }

  return Array.from(paths);
};

// Builds the UpdateV2 request directly from the form's current values - independent of
// `createSubmitPayload` (which is only used to reset the form via `populateSchema` after a
// successful save; its output isn't used to build this request).
export const buildCallQueueUpdateV2Payload = ({
  callQueueId,
  formValues,
  devices,
  holdMusicSystemMedia,
  paths,
}: {
  callQueueId: string;
  formValues: FormValues<typeof schema>;
  devices: CallQueueDevice[];
  holdMusicSystemMedia: boolean;
  paths: string[];
}): UpdateV2Request => {
  const {
    name,
    deviceRingType,
    greetingEnabled,
    greetingMediaId,
    allowExit,
    holdMusicId,
    positionAnnouncementEnabled,
    firstAnnouncementDelayMs,
    repeatIntervalMs,
    maxWaitTimeMs,
    locationIds,
  } = formValues;

  // The UpdateV2 API has no separate "enabled" flag for the greeting/position announcement -
  // unlike the v1 Update API. An empty greetingMediaId and a null positionAnnouncement ARE the
  // "disabled" signal, so force them when the corresponding checkbox is unchecked, regardless
  // of whatever value the (now-hidden) greetingMediaId dropdown / delay fields still hold.
  const resolvedGreetingMediaId = greetingEnabled ? (greetingMediaId === 'default' ? '' : greetingMediaId ?? '') : '';

  const positionAnnouncement = positionAnnouncementEnabled
    ? {
        firstAnnouncementDelayMs: parseInt(firstAnnouncementDelayMs || '0') * 1000,
        repeatIntervalMs: parseInt(repeatIntervalMs || '0') * 1000,
      }
    : null;

  return {
    callQueueId,
    locationIds,
    name,
    ringType: deviceRingType as CallQueueRingType,
    greetingMediaId: resolvedGreetingMediaId,
    isHoldMusicSystemMedia: holdMusicSystemMedia,
    allowExit,
    // `null` (not `undefined`) is required here - the generated type doesn't declare it as
    // assignable, but it's the value the API needs to clear the field when disabled.
    positionAnnouncement: positionAnnouncement as PositionAnnouncementOptions,
    holdMusicId: holdMusicId === 'default' ? '' : holdMusicId ?? '',
    maxWaitTimeMs: parseInt(maxWaitTimeMs || '0') * 1000 * 60,
    devices,
    updateMask: new FieldMask({ paths }),
  };
};

const formatFirstAnnouncement = (time: number) => {
  if (time === 0) {
    return i18next.t('Immediate', { ns: 'phone' });
  }

  return `${time}s`;
};

const FIRST_ANNOUNCEMENT_DELAY_OPTIONS_STEP = 5;
const FIRST_ANNOUNCEMENT_DELAY_OPTIONS_MAX = 60;
export const FIRST_ANNOUNCEMENT_DELAY_OPTIONS = Array.from(
  { length: FIRST_ANNOUNCEMENT_DELAY_OPTIONS_MAX / FIRST_ANNOUNCEMENT_DELAY_OPTIONS_STEP + 1 },
  (_, i) => i * FIRST_ANNOUNCEMENT_DELAY_OPTIONS_STEP
).map((num) => ({
  value: num.toString(),
  label: formatFirstAnnouncement(num),
}));

const formatRepeatInterval = (time: number) => {
  if (time === 0) {
    return i18next.t('Never', { ns: 'phone' });
  }
  const min = Math.floor(time / 60);
  const sec = time % 60;
  const minuteStr = min > 0 ? `${min}m` : '';
  const secondStr = sec > 0 ? `${sec}s` : '';
  return `${minuteStr} ${secondStr}`.trim();
};

const REPEAT_INTERVAL_OPTIONS_STEP = 30;
const REPEAT_INTERVAL_OPTIONS_MAX = 180;
export const REPEAT_INTERVAL_OPTIONS = Array.from(
  { length: REPEAT_INTERVAL_OPTIONS_MAX / REPEAT_INTERVAL_OPTIONS_STEP + 1 },
  (_, i) => i * REPEAT_INTERVAL_OPTIONS_STEP
).map((num) => ({
  value: num.toString(),
  label: formatRepeatInterval(num),
}));
